import React, { useEffect, useRef, useState } from 'react';
import './SendMailPanel.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import CustomTogglebox from '../input/CustomTogglebox';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import MailAudienceEditor from './MailAudienceEditor';
import ProcessNotice from './ProcessNotice';
import {
  apiErrorMessage, eventNouns, eventShortLabel, formatDate, formatDateTime, isOpenEvent, NEWSLETTER_TARGET_KEY,
  newExecutionId,
} from './processUtils';

const RESEND_POPUP = 'processresendmail';

// Mode of a send: Initial (step not done yet), Resend (same mail again), CatchUp (only uncovered events).
const MODE = { Initial: 'Initial', Resend: 'Resend', CatchUp: 'CatchUp' };

const hasText = (html) => String(html ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';

// Preview errors about a missing textblock belong to the affected accordion item: matched by one of
// the audience's names, or — unambiguous — the only affected audience takes the only unmatched one.
// Returns { byAudience: { [targetKey]: text }, general: [text] }.
function splitPreviewErrors(errors, missingAudiences, namesOf) {
  const byAudience = {};
  const general = [];
  const unmatched = [];
  errors.forEach(text => {
    if (missingAudiences.length === 0 || !/textbaustein/i.test(text)) { general.push(text); return; }
    const lower = text.toLowerCase();
    const hit = missingAudiences.find(a => !byAudience[a.targetKey]
      && namesOf(a).some(name => lower.includes(name.toLowerCase())));
    if (hit) byAudience[hit.targetKey] = text;
    else unmatched.push(text);
  });
  const open = missingAudiences.filter(a => !byAudience[a.targetKey]);
  if (unmatched.length === 1 && open.length === 1) byAudience[open[0].targetKey] = unmatched[0];
  else general.push(...unmatched);
  return { byAudience, general };
}

/**
 * Step kind SendMail: GET Steps/{stepId}/Preview → accordion per audience with editable subject + text
 * and "Testmail an mich" → POST Execute { executionId (fresh per click), mode, eventIds,
 * confirmUnreleased, audiences }. 409 (already executed) → the step is reloaded and the panel offers
 * "Erneut senden" (Resend) or "Nachtrag senden" (CatchUp, uncovered events only). Done steps show the
 * last dispatch and a banner for events that are not in the mail yet.
 */
function SendMailPanel({
  process, step, active, execMode, activeActions, resetAction, onStartMode, onFinished, onProcess,
  onReload, groupsById, reportError,
}) {
  const { openPopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [drafts, setDrafts] = useState({});           // { [targetKey]: { subject, body } }
  const [contentVersion, setContentVersion] = useState(0);
  const [expanded, setExpanded] = useState(() => new Set());
  const [confirmUnreleased, setConfirmUnreleased] = useState(false);
  const [notice, setNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [testSending, setTestSending] = useState(null); // targetKey
  const [testErrors, setTestErrors] = useState({});
  const [conflict, setConflict] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [marking, setMarking] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);  // "Erneut laden" after a failed preview
  // Key of the loaded preview: "a:<open event ids>" (Initial/Resend share it, so edits survive a 409)
  // or "c:<uncovered ids>" (CatchUp). Changes of the event set reload the texts.
  const loadedKeyRef = useRef(null);
  const requestRef = useRef(0);

  const nouns = eventNouns(process);
  const events = process.events ?? [];
  const eventsById = new Map(events.map(e => [e.id, e]));
  const uncovered = (step.uncoveredEventIds ?? []).map(Number);
  const isCatchUp = execMode === MODE.CatchUp;
  const previewIds = isCatchUp
    ? [...uncovered].sort((a, b) => a - b)
    : events.filter(isOpenEvent).map(e => e.id).sort((a, b) => a - b);
  const previewKey = `${isCatchUp ? 'c' : 'a'}:${previewIds.join(',')}`;

  useEffect(() => {
    if (!active || loadedKeyRef.current === previewKey) return;
    const previousKey = loadedKeyRef.current;
    loadedKeyRef.current = previewKey;
    const [family, idList] = previewKey.split(':');
    // Same send kind but another event set → the event block of the texts is outdated.
    const eventsChanged = previousKey != null && previousKey.startsWith(`${family}:`);

    const params = new URLSearchParams();
    if (family === 'c') idList.split(',').filter(Boolean).forEach(id => params.append('eventIds', id));
    const query = params.toString();
    const request = ++requestRef.current;

    setLoading(true);
    setLoadError('');
    setSendError('');
    api.get(`/EventProcess/${process.id}/Steps/${step.stepId}/Preview${query ? `?${query}` : ''}`)
      .then(r => {
        if (request !== requestRef.current) return;
        const data = r.data;
        const nextDrafts = {};
        (data.audiences ?? []).forEach(a => { nextDrafts[a.targetKey] = { subject: a.subject ?? '', body: a.body ?? '' }; });
        setPreview(data);
        setDrafts(nextDrafts);
        setContentVersion(v => v + 1);
        // First audience open, plus every audience that cannot be sent (no textblock).
        setExpanded(new Set((data.audiences ?? [])
          .filter((a, i) => i === 0 || a.textblockId == null)
          .map(a => a.targetKey)));
        setConfirmUnreleased(false);
        setTestErrors({});
        setNotice(eventsChanged ? `Die ${nouns.many} des Prozesses haben sich geändert – Betreff und Text wurden neu geladen.` : '');
      })
      .catch(err => {
        if (request !== requestRef.current) return;
        setPreview(null);
        loadedKeyRef.current = null;
        setLoadError(apiErrorMessage(err, 'Vorschau konnte nicht geladen werden.'));
      })
      .finally(() => { if (request === requestRef.current) setLoading(false); });
  }, [active, previewKey, process.id, step.stepId, nouns.many, reloadToken]);

  const audiences = preview?.audiences ?? [];
  const totalRecipients = audiences.reduce((sum, a) => sum + (a.recipientCount ?? 0), 0);
  const unreleased = (preview?.unreleasedEventIds ?? []).map(Number);
  const errors = preview?.errors ?? [];
  const warnings = preview?.warnings ?? [];
  const targetsByKey = new Map((step.targets ?? []).map(t => [t.targetKey, t]));
  const groupOf = (target) => (target?.participantGroupId != null ? groupsById.get(Number(target.participantGroupId)) : null);

  // No default textblock anymore: an audience without one (step target and mail rule empty) cannot be
  // sent. Its preview error is shown on its accordion item, the send button stays blocked.
  const missingAudiences = audiences.filter(a => a.textblockId == null);
  const missingKeys = new Set(missingAudiences.map(a => a.targetKey));
  const { byAudience: audienceErrors, general: generalErrors } = splitPreviewErrors(errors, missingAudiences, a => {
    const target = targetsByKey.get(a.targetKey);
    const group = groupOf(target);
    return [a.targetName, target?.participantGroupName, group?.name, group?.pluralName,
      a.targetKey === NEWSLETTER_TARGET_KEY ? 'Newsletter' : null].filter(Boolean);
  });

  const updateDraft = (targetKey, patch) => {
    setDrafts(prev => ({ ...prev, [targetKey]: { ...(prev[targetKey] ?? {}), ...patch } }));
  };

  const toggleExpanded = (targetKey) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(targetKey)) next.delete(targetKey); else next.add(targetKey);
      return next;
    });
  };

  // Client checks before any send; returns false and shows the reason.
  const validate = () => {
    if (!preview) return false;
    if (missingAudiences.length > 0) {
      const first = missingAudiences[0];
      setExpanded(prev => new Set(prev).add(first.targetKey));
      setSendError(`Für „${first.targetName}" ist kein Textbaustein zugewiesen – so kann nicht gesendet werden.`);
      return false;
    }
    if (errors.length > 0) { setSendError(errors[0]); return false; }
    if (audiences.length === 0) { setSendError('Dieser Schritt hat keine Zielgruppe.'); return false; }
    const incomplete = audiences.find(a => !(drafts[a.targetKey]?.subject ?? '').trim() || !hasText(drafts[a.targetKey]?.body));
    if (incomplete) {
      setExpanded(prev => new Set(prev).add(incomplete.targetKey));
      setSendError(`Bitte Betreff und Text für „${incomplete.targetName}" ausfüllen.`);
      return false;
    }
    if (totalRecipients === 0) { setSendError('Keine Empfänger vorhanden – die Mail würde niemanden erreichen.'); return false; }
    if (unreleased.length > 0 && !confirmUnreleased) {
      setSendError('Bitte bestätige den Versand trotz nicht freigegebener Events.');
      return false;
    }
    return true;
  };

  const doSend = (mode) => {
    if (sending || !preview) return;
    setSending(true);
    setSendError('');
    setConflict('');
    startSuccessBarLoading(mode === MODE.CatchUp ? 'Nachtrag wird in die Versand-Queue gestellt' : 'Mails werden in die Versand-Queue gestellt');
    api.post(`/EventProcess/${process.id}/Steps/${step.stepId}/Execute`, {
      // One id per click: retries/double clicks of the same click never send twice.
      executionId: newExecutionId(),
      mode,
      eventIds: preview.eventIds,
      confirmUnreleased,
      audiences: audiences.map(a => ({
        targetKey: a.targetKey,
        subject: drafts[a.targetKey]?.subject ?? '',
        body: drafts[a.targetKey]?.body ?? '',
      })),
    })
      .then(response => {
        const result = response.data;
        setLastResult(result);
        startSuccessBarSuccess(result.duplicate
          ? 'Wurde bereits gesendet – es wurden keine Mails erneut verschickt'
          : `${result.enqueuedCount} Mails werden im Hintergrund versendet`);
        onFinished(result.process, result.warnings);
      })
      .catch(err => {
        closeSuccessBar();
        if (err?.response?.status === 409) {
          // Executed meanwhile (other tab/user): refresh the step, then offer Resend / CatchUp.
          setConflict(apiErrorMessage(err, 'Dieser Schritt wurde bereits ausgeführt.'));
          onReload();
          return;
        }
        setSendError(apiErrorMessage(err, 'Mails konnten nicht gesendet werden.'));
      })
      .finally(() => setSending(false));
  };

  const handleSend = () => {
    if (!validate()) return;
    if (execMode === MODE.Resend) {
      openPopup(RESEND_POPUP, step.stepId);
      return;
    }
    doSend(execMode);
  };

  const handleTestMail = (audience) => {
    const draft = drafts[audience.targetKey] ?? {};
    if (!(draft.subject ?? '').trim() || !hasText(draft.body)) {
      setTestErrors(prev => ({ ...prev, [audience.targetKey]: 'Bitte zuerst Betreff und Text ausfüllen.' }));
      return;
    }
    setTestSending(audience.targetKey);
    setTestErrors(prev => ({ ...prev, [audience.targetKey]: '' }));
    api.post(`/EventProcess/${process.id}/Steps/${step.stepId}/TestMail`, {
      targetKey: audience.targetKey,
      subject: draft.subject,
      body: draft.body,
      eventIds: preview?.eventIds ?? null,
    })
      .then(() => startSuccessBarSuccess('Testmail an dich wird versendet'))
      .catch(err => setTestErrors(prev => ({
        ...prev,
        [audience.targetKey]: apiErrorMessage(err, 'Testmail konnte nicht gesendet werden.'),
      })))
      .finally(() => setTestSending(null));
  };

  const handleMarkCovered = () => {
    if (marking || uncovered.length === 0) return;
    setMarking(true);
    startSuccessBarLoading('Wird als erledigt markiert');
    api.post(`/EventProcess/${process.id}/Steps/${step.stepId}/MarkCovered`, { eventIds: uncovered })
      .then(response => {
        startSuccessBarSuccess('Als erledigt markiert');
        setConflict('');
        onProcess(response.data, { resetView: false });
      })
      .catch(err => {
        closeSuccessBar();
        reportError(apiErrorMessage(err, 'Markieren fehlgeschlagen.'));
      })
      .finally(() => setMarking(false));
  };

  const uncoveredEvents = uncovered.map(id => eventsById.get(id)).filter(Boolean);
  const lastDispatch = step.lastDispatch;

  // ── Done / skipped: summary, "Nachtrag" banner, resend ─────────────────────────────────────────
  if (!active) {
    const skipped = step.status === 'skipped';
    return (
      <React.Fragment>
        {skipped ? (
          <p className={'processActionDesc'}>Dieser Schritt wurde übersprungen – die Mail wurde nicht gesendet.</p>
        ) : lastDispatch ? (
          <div className={'processMailStats'}>
            <span className={'processMailStat'}>
              <span className={'material-symbols-rounded'} aria-hidden={'true'}>schedule_send</span>
              Gesendet am {formatDateTime(lastDispatch.sentAt)}
            </span>
            <span className={'processMailStat'}>
              <span className={'material-symbols-rounded'} aria-hidden={'true'}>group</span>
              {lastDispatch.recipientCount} Empfänger
            </span>
            {lastDispatch.skippedCount > 0 && (
              <span className={'processMailStat isWarning'}>
                <span className={'material-symbols-rounded'} aria-hidden={'true'}>block</span>
                {lastDispatch.skippedCount} übersprungen (Platzhalter)
              </span>
            )}
            {lastDispatch.dedupCount > 0 && (
              <span className={'processMailStat'}>
                <span className={'material-symbols-rounded'} aria-hidden={'true'}>how_to_reg</span>
                {lastDispatch.dedupCount} Abonnenten bereits als Spieler erreicht
              </span>
            )}
          </div>
        ) : (
          <p className={'processActionDesc'}>
            Dieser Schritt ist erledigt{step.executedAt ? ` (am ${formatDateTime(step.executedAt)})` : ''}.
          </p>
        )}

        {lastResult?.warnings?.length > 0 && (
          <ProcessNotice tone={'warning'}>
            <ul>{lastResult.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </ProcessNotice>
        )}

        {conflict && (
          <ProcessNotice
            tone={'warning'}
            title={'Bereits ausgeführt'}
            actions={(
              <React.Fragment>
                <ActionButton icon={'replay'} label={'Erneut senden'} type={4} onClick={() => { setConflict(''); onStartMode(MODE.Resend); }} />
                {uncoveredEvents.length > 0 && (
                  <ActionButton icon={'forward_to_inbox'} label={'Nachtrag senden'} type={3} onClick={() => { setConflict(''); onStartMode(MODE.CatchUp); }} />
                )}
              </React.Fragment>
            )}
          >
            {conflict}
          </ProcessNotice>
        )}

        {!skipped && uncoveredEvents.length > 0 && (
          <ProcessNotice
            tone={'warning'}
            title={`${uncoveredEvents.length} ${uncoveredEvents.length === 1 ? 'Event' : 'Events'} nicht enthalten`}
            actions={(
              <React.Fragment>
                <ActionButton icon={'forward_to_inbox'} label={'Nachtrag senden'} type={3} onClick={() => onStartMode(MODE.CatchUp)} />
                <ActionButton icon={'done_all'} label={'Als erledigt markieren'} onClick={handleMarkCovered} disabled={marking} />
              </React.Fragment>
            )}
          >
            Diese {uncoveredEvents.length === 1 ? 'kam' : 'kamen'} nach dem Versand dazu und
            {uncoveredEvents.length === 1 ? ' ist' : ' sind'} nicht in der Mail
            {lastDispatch ? ` vom ${formatDate(lastDispatch.sentAt)}` : ''} enthalten:
            <ul>{uncoveredEvents.map(e => <li key={e.id}>{eventShortLabel(e)}</li>)}</ul>
          </ProcessNotice>
        )}

        <div className={'processActionButtons'}>
          {skipped ? (
            <ActionButton icon={'send'} label={'Jetzt senden'} type={3} onClick={() => onStartMode('run')} />
          ) : (
            <ActionButton icon={'replay'} label={'Erneut senden'} onClick={() => onStartMode(MODE.Resend)} />
          )}
          {resetAction}
        </div>
        {!skipped && (
          <p className={'processActionHint'}>Erneutes Senden schickt die Mail noch einmal an alle Empfänger.</p>
        )}
      </React.Fragment>
    );
  }

  // ── Compose + send ──────────────────────────────────────────────────────────────────────────
  const includedEvents = (preview?.eventIds ?? []).map(id => eventsById.get(Number(id))).filter(Boolean);
  const sendLabel = execMode === MODE.Resend
    ? `Erneut an ${totalRecipients} Empfänger senden`
    : execMode === MODE.CatchUp
      ? `Nachtrag an ${totalRecipients} Empfänger senden`
      : `An ${totalRecipients} Empfänger senden`;

  return (
    <React.Fragment>
      <p className={'processActionDesc'}>
        Die Mail geht an die Zielgruppen dieses Schritts – jeder Empfänger bekommt eine eigene, persönliche Mail.
        Betreff und Text kommen aus dem Textbaustein und lassen sich hier vor dem Versand anpassen.
      </p>

      {execMode === MODE.Resend && (
        <ProcessNotice tone={'warning'} title={'Erneut senden'}>
          Die Mail geht noch einmal an alle Empfänger – auch an die, die sie schon bekommen haben.
        </ProcessNotice>
      )}
      {execMode === MODE.CatchUp && (
        <ProcessNotice tone={'info'} title={'Nachtrag'}>
          Die Mail enthält nur die {nouns.many}, die beim letzten Versand noch nicht dabei waren.
        </ProcessNotice>
      )}
      {notice && <ProcessNotice tone={'info'}>{notice}</ProcessNotice>}
      {loadError && (
        <ProcessNotice
          tone={'error'}
          actions={<ActionButton icon={'refresh'} label={'Erneut laden'} onClick={() => setReloadToken(t => t + 1)} />}
        >
          {loadError}
        </ProcessNotice>
      )}
      {generalErrors.map((e, i) => <ProcessNotice key={`e${i}`} tone={'error'}>{e}</ProcessNotice>)}
      {warnings.length > 0 && (
        <ProcessNotice tone={'warning'}>
          <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </ProcessNotice>
      )}

      {loading && !preview ? (
        <p className={'processEmpty'}>Empfänger und Texte werden geladen …</p>
      ) : preview && (
        <React.Fragment>
          {includedEvents.length > 0 && (
            <div className={'processMailEvents'}>
              <span className={'processMailEventsLabel'}>Enthaltene {nouns.many}</span>
              {includedEvents.map(e => (
                <span key={e.id} className={`processMailEventChip${unreleased.includes(e.id) ? ' isUnreleased' : ''}`}>
                  {eventShortLabel(e)}
                </span>
              ))}
            </div>
          )}

          {unreleased.length > 0 && (
            <ProcessNotice tone={'warning'} title={'Nicht für alle Zielgruppen freigegeben'}>
              Diese {nouns.many} sind für mindestens eine Zielgruppe noch nicht freigegeben – Empfänger dieser
              Gruppen sehen sie erst nach der Freigabe:
              <ul>
                {unreleased.map(id => <li key={id}>{eventShortLabel(eventsById.get(id)) || `#${id}`}</li>)}
              </ul>
              <div className={'processMailConfirm'}>
                <CustomTogglebox
                  placeholder={'Trotzdem senden'}
                  checked={confirmUnreleased}
                  onChange={e => setConfirmUnreleased(e.target.checked)}
                />
              </div>
            </ProcessNotice>
          )}

          <div className={'processMailAudiences'}>
            {audiences.length === 0 && <p className={'processEmpty'}>Dieser Schritt hat keine Zielgruppe.</p>}
            {audiences.map(a => {
              const target = targetsByKey.get(a.targetKey);
              return (
                <MailAudienceEditor
                  key={a.targetKey}
                  audience={a}
                  target={target}
                  group={groupOf(target)}
                  missingTextblock={missingKeys.has(a.targetKey)}
                  missingText={audienceErrors[a.targetKey]}
                  draft={drafts[a.targetKey]}
                  contentVersion={contentVersion}
                  expanded={expanded.has(a.targetKey)}
                  onToggle={() => toggleExpanded(a.targetKey)}
                  onChange={patch => updateDraft(a.targetKey, patch)}
                  onTestMail={() => handleTestMail(a)}
                  testSending={testSending === a.targetKey}
                  testError={testErrors[a.targetKey]}
                  disabled={sending}
                />
              );
            })}
          </div>
        </React.Fragment>
      )}

      {sendError && <ProcessNotice tone={'error'}>{sendError}</ProcessNotice>}

      <div className={'processActionButtons'}>
        <ActionButton
          icon={'send'}
          label={sendLabel}
          type={2}
          onClick={handleSend}
          disabled={sending || loading || !preview || errors.length > 0 || missingAudiences.length > 0}
        />
        {activeActions}
      </div>
      {preview && missingAudiences.length > 0 && (
        <p className={'processActionHint'}>
          Senden ist erst möglich, wenn jede Zielgruppe einen Textbaustein hat – siehe die markierten Zielgruppen.
        </p>
      )}

      <Popup
        name={RESEND_POPUP}
        confirmText={'Erneut senden'}
        headerText={'Mail erneut senden'}
        type={'warning'}
        onConfirm={() => doSend(MODE.Resend)}
        withCancel
      >
        <p>
          Die Mail <b>„{step.label}"</b> geht noch einmal an <b>{totalRecipients} Empfänger</b>.
        </p>
        <p className={'formFieldHint'}>Bereits versendete Mails werden nicht zurückgeholt.</p>
      </Popup>
    </React.Fragment>
  );
}

export default SendMailPanel;
